/**
 * Progress Notes + Incidents export — clean DOCUMENT (prose) format, NOT a table.
 *
 * Renders one readable entry per note/incident in chronological order, with the
 * org's branded letterhead (logo / name / ABN / address / contact) at the top.
 * Used by the client Communications page. Pass a pre-merged list of notes AND
 * incidents so a single download contains both.
 *
 *   items: [{ kind: 'note'|'incident', data: <row> }]   (any order — sorted here)
 */
import jsPDF from 'jspdf';
import {
  Document, Packer, Paragraph, TextRun, BorderStyle,
} from 'docx';
import { fmtTime12 } from './time';
import { renderPdfHeader, brandRgb, brandHex, brandingLines } from './exportBranding';

// ── shared helpers ──────────────────────────────────────────────────────────
const MOOD_LABELS  = ['', 'Very Upset', 'Upset', 'Neutral', 'Happy', 'Very Happy'];
const BEHAV_LABELS = ['', 'Distressed', 'Unsettled', 'Neutral', 'Calm', 'Excellent'];
const INCIDENT_CAT = {
  category_1: 'Category 1', category_2: 'Category 2', category_3: 'Category 3',
  category_4: 'Category 4', category_5: 'Category 5',
};

const cap = (s) => { const t = String(s || '').trim(); return t ? t.charAt(0).toUpperCase() + t.slice(1).toLowerCase() : ''; };

// Long date from a date-only string (no timezone shift) or a full timestamp.
function longDate(d) {
  if (!d) return '';
  const s = String(d);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  const dt = m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(s);
  if (isNaN(dt)) return s.slice(0, 10);
  return dt.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
}
function clockTime(d) {
  if (!d) return '';
  const dt = new Date(d);
  if (isNaN(dt)) return '';
  return dt.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', hour12: true });
}
function genStamp() {
  try { return new Date().toLocaleString('en-AU', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }); }
  catch { return new Date().toISOString(); }
}

// Sort on the day the entry is ABOUT, never the day it was typed. The header of a note already
// showed its shift date, so ordering by created_at put a note headed "Fri 4 Sep" after entries
// from the 19th purely because it was written up late — the export read as out of order.
//
// Within a day the order is the order the work happened: the morning shift's note, then the
// afternoon's, then the sleepover. A note takes its time from its shift and an incident from
// its own; anything untimed falls to the end of its day.
const SECS_IN_DAY = 86400;

function secondsIntoDay(hhmmss) {
  const m = /^(\d{1,2}):(\d{2})(?::(\d{2}))?/.exec(String(hhmmss || ''));
  if (!m) return null;
  return (+m[1]) * 3600 + (+m[2]) * 60 + (+(m[3] || 0));
}

function entryTs(item) {
  const d = item.data;
  const isInc = item.kind === 'incident';
  const day = isInc ? (d.incident_date || d.created_at) : (d.note_date || d.shift_date || d.created_at);
  const t = new Date(String(day).slice(0, 10));
  if (isNaN(t)) return 0;
  // Prefer the shift's (or incident's) own clock time; fall back to the write time, and put an
  // entry with neither at the very end of its day.
  const secs = secondsIntoDay(isInc ? d.incident_time : d.start_time)
    ?? secondsIntoDay(String(d.created_at || '').slice(11, 19))
    ?? SECS_IN_DAY;
  // Milliseconds for the day, plus the time of day as milliseconds — never enough to cross into
  // the next day, so the day ordering is never disturbed.
  return t.getTime() + secs * 1000;
}

// Build the plain-text detail lines + body for one entry (shared by PDF + DOCX).
function entryModel(item, index) {
  const d = item.data;
  const num = `${index + 1}.`;
  if (item.kind === 'incident') {
    const catL = INCIDENT_CAT[d.category] || cap(d.category) || 'Incident';
    const when = `${longDate(d.incident_date || d.created_at)}${d.incident_time ? ' · ' + fmtTime12(d.incident_time) : ''}`;
    const meta = [
      `Reported by ${d.staff_name || d.created_by_name || 'Staff'}`,
      d.status && `Status: ${cap(String(d.status).replace(/_/g, ' '))}`,
      d.location && `Location: ${d.location}`,
    ].filter(Boolean);
    return {
      num, isIncident: true,
      title: `Incident — ${catL}`,
      when,
      meta,
      tags: [],
      body: d.description || '—',
    };
  }
  // progress note
  const typeL = (() => { const s = cap(d.note_type || 'Progress'); return /note/i.test(s) ? s : `${s} Note`; })();
  const shift = d.shift_date
    ? `Shift: ${longDate(d.shift_date)}${d.start_time ? ' · ' + fmtTime12(d.start_time) : ''}${d.end_time ? ' – ' + fmtTime12(d.end_time) : ''}`
    : null;
  // The exported document carries the day the note covers and nothing about when it was typed —
  // a write-up date on the page only invites questions the reader cannot answer. It is still on
  // the note in the app, and in the shift-notes reports, for anyone who needs to audit it.
  const meta = [
    `By ${d.staff_name || 'Staff'}`,
    shift,
    d.is_private ? 'Private' : null,
  ].filter(Boolean);
  const tags = [];
  if (d.mood_rating > 0) tags.push(`Mood: ${MOOD_LABELS[d.mood_rating] || d.mood_rating} (${d.mood_rating}/5)`);
  if (d.behaviour_rating > 0) tags.push(`Behaviour: ${BEHAV_LABELS[d.behaviour_rating] || d.behaviour_rating} (${d.behaviour_rating}/5)`);
  const activities = Array.isArray(d.activities_completed) ? d.activities_completed : [];
  if (activities.length) tags.push(`Activities: ${activities.join(', ')}`);
  const atts = Array.isArray(d.attachments) ? d.attachments : [];
  if (atts.length) tags.push(`Attachments: ${atts.length} file${atts.length !== 1 ? 's' : ''}`);
  return {
    num, isIncident: false,
    title: typeL,
    when: longDate(d.note_date || d.shift_date || d.created_at),
    meta,
    tags,
    body: d.note_content || '—',
  };
}

function baseName(clientName) {
  return `progress-notes${clientName ? '-' + clientName.replace(/\s+/g, '-').toLowerCase() : ''}-${new Date().toISOString().slice(0, 10)}`;
}

// ═══════════════════════════════════════════════════════════════════════════
// PDF — clean prose layout (portrait), branded letterhead, page numbers.
// ═══════════════════════════════════════════════════════════════════════════
export function exportProgressPDF(items, { clientName = '', branding = {} } = {}) {
  const sorted = [...items].sort((a, b) => entryTs(a) - entryTs(b));
  if (!sorted.length) return;

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pw = doc.internal.pageSize.getWidth();
  const ph = doc.internal.pageSize.getHeight();
  const M = 14;
  const maxW = pw - M * 2;
  const { r, g, b } = brandRgb(branding);

  const noteCount = sorted.filter(i => i.kind === 'note').length;
  const incCount = sorted.filter(i => i.kind === 'incident').length;
  const subtitle = `${clientName ? clientName + '  ·  ' : ''}${noteCount} progress note${noteCount !== 1 ? 's' : ''}${incCount ? `  ·  ${incCount} incident${incCount !== 1 ? 's' : ''}` : ''}  ·  chronological order`;

  let y = renderPdfHeader(doc, branding, { reportTitle: 'Progress Notes & Incidents', subtitle });

  const ensure = (h) => { if (y + h > ph - 16) { doc.addPage(); y = 18; } };
  const write = (text, { size = 10, style = 'normal', color = [51, 65, 85], indent = 0, gap = 1.2 } = {}) => {
    doc.setFontSize(size); doc.setFont('helvetica', style); doc.setTextColor(...color);
    const lh = size * 0.42 + 0.6;
    const lines = doc.splitTextToSize(String(text == null ? '' : text), maxW - indent);
    for (const ln of lines) { ensure(lh); doc.text(ln, M + indent, y); y += lh; }
    y += gap;
  };

  sorted.forEach((item, i) => {
    const m = entryModel(item, i);
    ensure(18);
    // Coloured rule + entry title
    const titleColor = m.isIncident ? [185, 28, 28] : [r, g, b];
    doc.setDrawColor(...titleColor); doc.setLineWidth(0.6);
    ensure(2); doc.line(M, y, M + 8, y); y += 3.2;
    write(`${m.num}  ${m.title}`, { size: 12, style: 'bold', color: titleColor, gap: 0.8 });
    if (m.when) write(m.when, { size: 9.5, style: 'bold', color: [71, 85, 105], gap: 0.6 });
    if (m.meta.length) write(m.meta.join('   ·   '), { size: 8.8, color: [100, 116, 139], gap: 0.6 });
    if (m.tags.length) m.tags.forEach(t => write(t, { size: 8.8, color: [100, 116, 139], gap: 0.2 }));
    y += 1.2;
    // Body — preserve the worker's paragraph breaks
    String(m.body).split(/\r?\n/).forEach(line => write(line || ' ', { size: 10, color: [40, 51, 69], gap: line.trim() ? 0.4 : 0.4 }));
    y += 3.5;
  });

  // Footer page numbers
  const pages = doc.internal.getNumberOfPages();
  for (let p = 1; p <= pages; p++) {
    doc.setPage(p);
    doc.setFontSize(8); doc.setTextColor(148, 163, 184);
    doc.text(`Page ${p} of ${pages}`, pw / 2, ph - 7, { align: 'center' });
  }

  doc.save(`${baseName(clientName)}.pdf`);
}

// ═══════════════════════════════════════════════════════════════════════════
// DOCX — clean prose layout, branded text letterhead, no tables.
// ═══════════════════════════════════════════════════════════════════════════
export async function exportProgressDocx(items, { clientName = '', branding = {} } = {}) {
  const sorted = [...items].sort((a, b) => entryTs(a) - entryTs(b));
  if (!sorted.length) return;

  const hex = brandHex(branding);
  const orgName = (branding && branding.name) || 'Rostery';
  const lines = brandingLines(branding);
  const noteCount = sorted.filter(i => i.kind === 'note').length;
  const incCount = sorted.filter(i => i.kind === 'incident').length;

  const children = [];

  // Letterhead
  children.push(new Paragraph({
    children: [new TextRun({ text: orgName, bold: true, size: 34, color: hex })],
    spacing: { after: lines.length ? 40 : 120 },
  }));
  lines.forEach((ln, i) => children.push(new Paragraph({
    children: [new TextRun({ text: ln, size: 17, color: '64748B' })],
    spacing: { after: i === lines.length - 1 ? 120 : 20 },
  })));
  // Title + meta with a brand-coloured underline
  children.push(new Paragraph({
    children: [new TextRun({ text: 'Progress Notes & Incidents', bold: true, size: 28, color: '1E293B' })],
    border: { bottom: { color: hex, space: 4, size: 8, style: BorderStyle.SINGLE } },
    spacing: { after: 80 },
  }));
  children.push(new Paragraph({
    children: [new TextRun({
      text: `${clientName ? 'Client: ' + clientName + '   |   ' : ''}Generated ${genStamp()}   |   ${noteCount} progress note${noteCount !== 1 ? 's' : ''}${incCount ? `, ${incCount} incident${incCount !== 1 ? 's' : ''}` : ''} (chronological)`,
      size: 16, color: '94A3B8',
    })],
    spacing: { after: 240 },
  }));

  // Entries
  sorted.forEach((item, i) => {
    const m = entryModel(item, i);
    const titleColor = m.isIncident ? 'B91C1C' : hex;
    children.push(new Paragraph({
      children: [new TextRun({ text: `${m.num}  ${m.title}`, bold: true, size: 24, color: titleColor })],
      spacing: { before: 160, after: 20 },
    }));
    if (m.when) children.push(new Paragraph({
      children: [new TextRun({ text: m.when, bold: true, size: 19, color: '475569' })],
      spacing: { after: 10 },
    }));
    if (m.meta.length) children.push(new Paragraph({
      children: [new TextRun({ text: m.meta.join('   ·   '), size: 17, color: '64748B' })],
      spacing: { after: 10 },
    }));
    m.tags.forEach(t => children.push(new Paragraph({
      children: [new TextRun({ text: t, size: 17, color: '64748B' })],
      spacing: { after: 10 },
    })));
    // Body paragraphs
    String(m.body).split(/\r?\n/).forEach(line => children.push(new Paragraph({
      children: [new TextRun({ text: line || '', size: 21, color: '283345' })],
      spacing: { after: 60 },
    })));
  });

  const wordDoc = new Document({ sections: [{ children }] });
  const blob = await Packer.toBlob(wordDoc);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = `${baseName(clientName)}.docx`; a.click();
  URL.revokeObjectURL(url);
}
